import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {  useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useQuery } from '@apollo/client';
import { Box, Grid, Typography } from '@mui/material';
import { GET_STORE, GET_VENUE, GET_MENU } from '../graphql/query';
import { HOME_PAGE_ROUTE, STORES_LIST_ROUTE } from '../constants/routes';
import { Text } from '../elements/text';
import { Loader } from '../elements/loader';
import { Header } from '../components/header';
import { customTheme } from '../theme';
import { Image } from '../elements/image';
import { images } from '../constants/images';
import MenuItemsList from '../components/vendor-menu/items-list';
import { FloatingViewCartButton } from '../components/vendor-menu/floating-view-cart-button';
import {
  Menu,
  MenuDetails,
  Status,
  StoreWithDetails,
  Vendor,
} from '../types/vendor-menu';
import { Venue } from '../types';
import { CustomButton } from '../elements/button';
import { LocationText } from './location-text-display';
import { Select, MenuItem } from '@mui/material';
const VendorMenu = () => {
  const navigate = useNavigate();
  const [menu, setMenu] = useState<Menu | null>(null);
  const [store, setStore] = useState<StoreWithDetails | null>(null);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [venue, setVenue] = useState<Venue | null>(null);

  const { t } = useTranslation(['patron_portal', 'common']);

  const { vendorID, storeID, menuID } = useParams();
  const [currentMenuId, setCurrentMenuId] = useState<string | null>(
    menuID || null,
  );
  const [selectedMenu, setSelectedMenu] = useState<MenuDetails | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  // Fetch the menu data and set using setMenu action
  useQuery(GET_MENU, {
    variables: {
      id: menuID,
      vendorID: vendorID,
    },
    onCompleted: (data) => {
      if (data && data.VendorMenu) {
        setMenu(data.VendorMenu);
      }
      console.log('Menu data fetched successfully:', data);
    },
    onError: (error) => {
      console.error('Error fetching menu:', error);
    },
  });

  const { loading: getVendorLoading } = useQuery(GET_STORE, {
    onCompleted: (data) => {
      if (data && data?.getStore?.owner) {
        setStore(data.getStore);
      }
    },
    variables: {
      ownerID: vendorID,
      storeID: storeID,
    },
  });

  useQuery(GET_VENUE, {
    variables: {
      id: store?.owner?.venueID,
    },
    onCompleted: (data) => {
      setVenue(data.getVenue);
    },
  });

  const isDisabled = venue?.isDisabled || venue?.isDeleted;

  useEffect(() => {
    if (store?.isDisabled) {
      navigate(STORES_LIST_ROUTE);
      const message = t('patron_portal.vendor.notAvailableMsg', {
        ns: 'patron_portal',
        tradingName: store.displayName,
      });
      enqueueSnackbar(message, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      });
    }
  }, [store]);

  const availableMenus = store?.menus.filter(
    (menu) =>
      menu.menu.status === Status.Active ||
      menu.menu.status === Status.NoSchedule,
  );
  useEffect(() => {
    if (store && store.menus.length > 0) {
      if (currentMenuId) {
        const found = store.menus.find((m) => m.menu.id === currentMenuId);
        setSelectedMenu(found || store.menus[0]);
      } else {
        setSelectedMenu(store.menus[0]);
      }
    }
  }, [store, currentMenuId]);

  if (getVendorLoading) {
    return <Loader visible={true} />;
  }

  const backIconRoute = STORES_LIST_ROUTE;

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Header
            backIconRoute={backIconRoute}
            middleComponent={
              <div>
                <Typography
                  fontFamily={customTheme.fontFamily}
                  fontSize={18}
                  fontWeight={600}
                >
                  {store?.displayName}
                </Typography>
                <Grid container alignItems="center" spacing={1} mt={0.5}>
                  <Grid item>
                    <LocationText />
                  </Grid>
                </Grid>
              </div>
            }
          />
        </Grid>
        {isDisabled && (
          <Grid item xs={12} paddingTop={`${20}px`}>
            <Box p={2} sx={{ textAlign: 'center' }} mt="10vh">
              <Text
                text={t('patron_portal.vendorListing.notOperatingMsg', {
                  ns: 'patron_portal',
                  venueName: venue.name,
                })}
                fontSize={16}
              />
            </Box>
            <Grid
              container
              item
              xs={12}
              lg={6}
              m="auto"
              p={2}
              paddingTop={`${40}px`}
            >
              <CustomButton
                style={{ marginTop: '40vh' }}
                title={t('common.home', { ns: 'common' })}
                onClick={() => navigate(HOME_PAGE_ROUTE)}
              />
            </Grid>
          </Grid>
        )}

        {!isDisabled && (
          <Grid
            container
            item
            md={8}
            margin="auto"
            sx={{ paddingBottom: '8vh' }}
          >
            <Grid item xs={12} mt={1}>
              <Image
                src={
                  selectedMenu?.menu.imageUri &&
                  selectedMenu.menu.imageUri !== ''
                    ? selectedMenu.menu.imageUri
                    : store?.storeFrontImageUrl ||
                      store?.owner?.imageUrl ||
                      images.VENDOR_DEFAULT_IMAGE
                }
              />
            </Grid>

            {availableMenus && availableMenus.length > 1 && (
              <Grid width={'100%'} xs={12}>
                <Box mt={2}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: 'text.primary', mb: 1, ml: 1 }}
                  >
                    Menu
                  </Typography>
                  <Grid width={'100%'} mb={2}>
                    <Select
                      value={selectedMenu?.menu.id || ''}
                      onChange={(e) => setCurrentMenuId(e.target.value)}
                      sx={{
                        borderRadius: '12px',
                        bgcolor: 'red',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px solid #ddd',
                        backgroundColor: '#fff',
                        height: 48,
                        fontSize: '1rem',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover': {
                          border: '1px solid #aaa',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 400,
                            borderRadius: '12px',
                          },
                        },
                      }}
                    >
                      {availableMenus.map((m) => {
                        return (
                          <MenuItem
                            key={m.menu.id}
                            value={m.menu.id}
                            sx={{
                              fontSize: '1rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {m.menu.description}{' '}
                            {m.menu?.discounts?.[0]?.discountAmount &&
                              `(${m.menu.discounts[0].discountAmount}% off)`}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Grid>
                </Box>
              </Grid>
            )}

            {availableMenus && availableMenus.length === 1 && (
              <Typography mt={2} fontWeight={500} mb={1} fontSize={20}>
                {availableMenus[0].menu.description}
              </Typography>
            )}
            {vendorID && store && store.menus.length > 0 && selectedMenu && (
              <MenuItemsList
                menuID={selectedMenu.menu.id}
                ownerID={vendorID}
                storeID={storeID}
              />
            )}
          </Grid>
        )}
        {!isDisabled && <FloatingViewCartButton />}
      </Grid>
    </>
  );
};

export default VendorMenu;
